import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/internships", label: "Internships" },
  { to: "/services", label: "Services" },
  { to: "/samai", label: "SamAI" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrolled = scrollY > 20;
  const compact = scrollY > 80;

  return (
    <header
      data-testid="site-navbar"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        backgroundColor: scrolled ? "rgba(255,255,255,0.93)" : "rgba(255,255,255,0.25)",
        borderBottom: `1px solid ${scrolled ? "rgba(0,0,0,0.08)" : "transparent"}`,
        boxShadow: scrolled ? "0 2px 32px rgba(0,0,0,0.06)" : "none",
        transition: "background-color 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease",
      }}
    >
      {/* Main bar */}
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          padding: "0 40px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: compact ? "54px" : "70px",
          transition: "height 0.35s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          data-testid="nav-logo"
          style={{ display: "flex", alignItems: "center", gap: "10px", textDecoration: "none" }}
        >
          <img
            src="/assets/scontinent-logo.png"
            alt="Scontinent Technologies"
            style={{
              width: compact ? "40px" : "54px",
              height: compact ? "40px" : "54px",
              objectFit: "contain",
              transition: "width 0.35s cubic-bezier(0.4,0,0.2,1), height 0.35s cubic-bezier(0.4,0,0.2,1)",
            }}
          />
          <div style={{ lineHeight: 1.1 }}>
            <div
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: compact ? "15px" : "18px",
                fontWeight: 600,
                color: "#0a0a0a",
                letterSpacing: "-0.02em",
                transition: "font-size 0.35s ease",
              }}
            >
              Scontinent
            </div>
            <div
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: "9px",
                textTransform: "uppercase",
                letterSpacing: "0.22em",
                color: "#737373",
                marginTop: "1px",
                transition: "opacity 0.3s ease",
                opacity: compact ? 0.6 : 1,
              }}
            >
              Technologies
            </div>
          </div>
        </Link>

        {/* Desktop nav links */}
        <nav style={{ alignItems: "center", gap: "2px" }} className="hidden lg:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              style={({ isActive }) => ({
                position: "relative",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: 500,
                color: isActive ? "#0a0a0a" : "#737373",
                textDecoration: "none",
                borderRadius: "99px",
                backgroundColor: "transparent",
                transition: "color 0.2s ease, background-color 0.2s ease",
              })}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "#0a0a0a";
                e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.05)";
              }}
              onMouseLeave={(e) => {
                const isActive = e.currentTarget.getAttribute("aria-current") === "page";
                e.currentTarget.style.color = isActive ? "#0a0a0a" : "#737373";
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {/* Active dot underline */}
                  <span
                    style={{
                      position: "absolute",
                      bottom: "5px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      height: "2px",
                      borderRadius: "99px",
                      backgroundColor: "#0a0a0a",
                      width: isActive ? "18px" : "0px",
                      opacity: isActive ? 1 : 0,
                      transition: "width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease",
                      display: "block",
                    }}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden lg:flex items-center gap-2">
          <Link to="/apply" data-testid="nav-apply-cta" className="btn-pill btn-pill--primary">
            Apply Now
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          style={{
            display: "inline-grid",
            placeItems: "center",
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            border: "1px solid rgba(0,0,0,0.12)",
            background: "transparent",
            cursor: "pointer",
            transition: "background 0.2s ease",
          }}
          className="lg:hidden"
        >
          <span
            style={{
              display: "grid",
              placeItems: "center",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
            }}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </span>
        </button>
      </div>

      {/* Mobile drawer — smooth slide down */}
      <div
        className="lg:hidden"
        style={{
          overflow: "hidden",
          maxHeight: open ? "480px" : "0px",
          opacity: open ? 1 : 0,
          transition: "max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease",
          backgroundColor: "rgba(255,255,255,0.98)",
          borderTop: open ? "1px solid rgba(0,0,0,0.06)" : "none",
        }}
      >
        <div style={{ padding: "16px 24px 24px" }}>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-mobile-link-${item.label.toLowerCase()}`}
              style={({ isActive }) => ({
                display: "block",
                padding: "14px 12px",
                fontSize: "16px",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? "#0a0a0a" : "#525252",
                textDecoration: "none",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                borderRadius: "8px",
                backgroundColor: isActive ? "rgba(0,0,0,0.03)" : "transparent",
                transition: "color 0.2s, background 0.2s",
              })}
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/apply"
            data-testid="nav-mobile-apply-cta"
            className="btn-pill btn-pill--primary"
            style={{ marginTop: "16px", width: "100%", justifyContent: "center" }}
          >
            Apply Now
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </header>
  );
}
